import React, { useEffect } from 'react';
import PropTypes, { any } from 'prop-types';
import { useDispatch, useSelector } from 'react-redux';
import _has from 'lodash/has';
import { createStructuredSelector } from 'reselect';
import Grid from '@material-ui/core/Grid';
import { makeStyles } from '@material-ui/core/styles';
import { Link } from 'react-router-dom';
import Typography from '@material-ui/core/Typography';
import _isEmpty from 'lodash/isEmpty';
import { makeSelectAuth } from 'containers/Auth/selectors';
import AppContainer from 'components/AppContainer';
import _Classnames from 'classnames';
import { makeSelectSavedTopics } from 'containers/Topics/selectors';
import { isTopicSaved } from 'containers/Topics/utils';
import { getSavedTopics, saveTopic } from 'containers/Topics/actions';
import ListTitle from 'components/ListTitle';
import useWindowSize from 'components/useWindowSize';
import { makeSelectHomeHeaders } from 'containers/Main/selectors';
import _get from 'lodash/get';
import Carousel from 'components/Carousel';
import { useQuery } from 'react-query';
import { trackFavoriteAction } from 'containers/Favorites/utils';
import TrendingTopic from './TrendingTopic';
import getTrendingTopics from '../services/getTrendingTopics';
import ArrowIcon from '../images/right-arrow.svg';

const useStyles = makeStyles(theme => ({
  root: {
    marginTop: 26,
    [theme.breakpoints.down('480')]: {
      position: 'relative',
    },
  },
  landingRoot: {
    marginTop: 18,
  },
  viewMore: {
    [theme.breakpoints.down('480')]: {
      position: 'absolute',
      bottom: 0,
      left: 0,
      width: '100%',
      textAlign: 'center',
      margin: 0,
      padding: '150px 0 40px 0 !important',
      backgroundImage:
        'linear-gradient(to bottom, rgba(255,255,255,0), rgba(255,255,255,0.74), rgba(255,255,255,1))',
    },
  },
  listWrapper: {
    paddingLeft: '0 !important',
    paddingRight: '0 !important',
    position: 'relative',
    '& .react-multi-carousel-track': {
      padding: '16px 0px',
    },
  },
  discoverLink: {
    textDecoration: 'none',
    ' & p': {
      marginLeft: 20,
      fontSize: 14,
      '& img': {
        marginBottom: 2,
      },
    },
  },
}));

const stateSelector = createStructuredSelector({
  auth: makeSelectAuth(),
  savedTopics: makeSelectSavedTopics(),
  homeHeaders: makeSelectHomeHeaders(),
});

function TopicsList(props) {
  const { onlyCards, topicsList } = props;
  const classes = useStyles();
  const { auth, savedTopics, homeHeaders } = useSelector(stateSelector);
  const dispatch = useDispatch();
  const { width } = useWindowSize();
  const isMobile = width <= 600;
  const isTablet = width <= 1024;
  const queryTopics = _isEmpty(topicsList);
  const limit = isMobile ? 30 : 60;
  const queryInfo = useQuery(
    queryTopics && ['home-topics', limit],
    getTrendingTopics,
  );

  useEffect(() => {
    if (_isEmpty(topicsList) || (auth.isLoaded && _isEmpty(topics))) {
      dispatch(getSavedTopics());
    }
  }, [auth, topicsList]);

  const onSaveTopic = (topicID, fields, saved, e) => {
    e.preventDefault();
    e.stopPropagation();

    const { slug, title, phi } = fields;

    let nodeID;
    if (saved) [{ nodeID }] = savedTopics.filter(el => el.sys.id === topicID);

    const data = {
      nodeID,
      topic: {
        topicID,
        slug,
        name: title,
      },
      save: !saved,
      phi,
    };

    trackFavoriteAction(
      'Card Click',
      {
        page: 'Home Page',
        type: 'Topics',
        resourceID: topicID,
        slug,
        name: title,
      },
      auth,
      saved,
    );

    dispatch(saveTopic(data));
    return false;
  };

  if (_isEmpty(topicsList) && _isEmpty(_get(queryInfo, 'data'))) {
    return null;
  }

  const responsive = {
    desktop: {
      breakpoint: {
        max: 3000,
        min: 1024,
      },
      items: 6,
    },
    mobile: {
      breakpoint: {
        max: 600,
        min: 0,
      },
      items: 2,
      partialVisibilityGutter: 15,
    },
    tablet: {
      breakpoint: {
        max: 1024,
        min: 600,
      },
      items: 4,
      partialVisibilityGutter: 25,
    },
  };

  const title = _get(homeHeaders, 'pageCopy.topics', '');
  const topics = {
    data: _get(queryInfo, 'data.data.topicsCollection.items'),
  };

  const { data } = topicsList || topics;
  const customProps = isTablet ? { partialVisbile: 'right' } : null;
  let finalSlideToShow;
  if (isMobile) {
    finalSlideToShow = 2;
  } else if (isTablet) {
    finalSlideToShow = 4;
  } else {
    finalSlideToShow = 6;
  }
  return (
    <AppContainer topPadding={onlyCards}>
      <Grid
        container
        spacing={2}
        className={_Classnames(
          classes.root,
          !_isEmpty(topicsList) ? classes.landingRoot : undefined,
        )}
        data-test-id={`home-${title.toLowerCase()}`}
      >
        <Grid item xs={12}>
          <Grid container direction="row" alignItems="center">
            <Grid item>
              <ListTitle title={title} />
            </Grid>
            {!onlyCards && (
              <Grid item>
                <Link to="/topics" className={classes.discoverLink}>
                  <Typography>
                    DISCOVER MORE <img src={ArrowIcon} alt="" />
                  </Typography>
                </Link>
              </Grid>
            )}
          </Grid>
        </Grid>
        <Grid item xs={12} className={classes.listWrapper}>
          <Carousel
            responsive={responsive}
            customProps={customProps}
            slideToShow={finalSlideToShow}
          >
            {data.map(topic => {
              const hasFields = _has(topic, 'fields');
              const saved = isTopicSaved(savedTopics, topic);
              const finalTopicFields = hasFields ? topic.fields : topic;
              return (
                <TrendingTopic
                  topicID={topic.id ? topic.id : topic.sys.id}
                  fields={finalTopicFields}
                  saved={saved}
                  onlyCards={onlyCards}
                  saveTopic={onSaveTopic}
                  key={finalTopicFields.slug}
                />
              );
            })}
          </Carousel>
        </Grid>
      </Grid>
    </AppContainer>
  );
}

TopicsList.propTypes = {
  onlyCards: PropTypes.bool,
  topicsList: any,
};

export default TopicsList;
